import { Head } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import {
  closing,
  hero,
  meta,
  trust,
  videoSections,
} from "@/components/case-studies/econo-pak-content"
import { LeadForm } from "@/components/landing/LeadForm"
import { SiteFooter } from "@/components/landing/SiteFooter"
import { SiteHeader } from "@/components/landing/SiteHeader"
import { Button } from "@/components/ui/button"
import { VideoTestimonial } from "@/components/ui/video-testimonial"
import { cn } from "@/lib/utils"

// Econo-Pak case study. A dedicated public landing page for paid campaigns and
// prospect/LinkedIn links. Light-only (the `landing-scope` wrapper re-declares
// the light palette). The lead form sits in the closing section; the hero and
// header both scroll down to it via #apply. Copy lives in
// components/case-studies/econo-pak-content.ts (DRAFT until Matt signs off).
export default function EconoPak() {

  return (
    <>
      <Head title={meta.title}>
        <meta name="description" content={meta.description} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:type" content="article" />
      </Head>

      <PublicShell>
        <div className="landing-scope min-h-dvh bg-page">
          <SiteHeader />

          <main>
            {/* Hero: metric-led, drives down to the form. */}
            <section
              id="top"
              data-section="hero"
              className="scroll-mt-24 border-b border-hairline bg-page"
            >
              <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
                <div className="max-w-3xl">
                  <span className="section-eyebrow">{hero.eyebrow}</span>
                  <h1 className="hero-title mt-3">{hero.title}</h1>
                  <p className="section-lead mt-5 max-w-2xl">{hero.subhead}</p>
                </div>

                <div className="mt-10">
                  <Button asChild size="lg">
                    <a href={hero.ctaHref} data-track-click="case_study_hero_apply">
                      {hero.ctaLabel}
                    </a>
                  </Button>
                </div>
              </div>
            </section>

            {/* Narrative arc: each section pairs prose with a video + pulled quote. */}
            {videoSections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                data-section={section.id}
                className={cn(
                  "scroll-mt-24 border-b border-hairline",
                  section.bg === "surface" ? "bg-surface" : "bg-page",
                )}
              >
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
                  <div className="max-w-2xl">
                    <span className="section-eyebrow">{section.eyebrow}</span>
                    <h2 className="section-title mt-3">{section.heading}</h2>
                    {section.lead.map((paragraph, i) => (
                      <p key={i} className="section-lead mt-4">
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  <div className="mt-10">
                    <VideoTestimonial
                      videoSrc={section.video.src}
                      poster={section.video.poster}
                      subheader={section.video.subheader}
                      impactTag={section.video.impactTag}
                      impactFacts={section.video.impactFacts}
                      quote={section.video.quote}
                      name={section.video.name}
                      title={section.video.title}
                      reverse={section.reverse}
                      playbackRate={section.video.playbackRate}
                    />
                  </div>
                </div>
              </section>
            ))}

            {/* Quiet trust band: reliability + how Matt works. */}
            <section
              data-section="trust"
              className="scroll-mt-24 border-b border-hairline bg-page"
            >
              <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-20">
                <blockquote className="testimonial-quote">{trust.quote}</blockquote>
                <p className="mt-4">
                  <strong>{trust.name}</strong>
                </p>
                <p className="meta">{trust.title}</p>
                <div className="mx-auto my-10 max-w-xs border-t border-hairline" />
                <p className="section-lead mx-auto max-w-xl">{trust.matt}</p>
              </div>
            </section>

            {/* Closing CTA: the application form. #apply lives on the form itself. */}
            <section data-section="closing" className="bg-surface">
              <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-20">
                <div className="text-center">
                  <span className="section-eyebrow">{closing.eyebrow}</span>
                  <h2 className="section-title mt-3">{closing.heading}</h2>
                  <p className="section-lead mx-auto mt-4 max-w-xl">{closing.lead}</p>
                </div>
                <div className="mt-10">
                  <LeadForm
                    source="econo-pak-case-study"
                    returnTo="/case-studies/econo-pak"
                  />
                </div>
              </div>
            </section>
          </main>

          <SiteFooter
            navLinks={[
              { href: "#results", label: "The results" },
              { href: "/", label: "Back to home" },
            ]}
          />
        </div>
      </PublicShell>
    </>
  )
}
